import { z } from 'zod'

export const socialPlatformSchema = z.enum(['facebook', 'instagram'])
export type SocialPlatform = z.infer<typeof socialPlatformSchema>

/** Platforms the crossposting feature currently supports, in display order. */
export const SUPPORTED_SOCIAL_PLATFORMS: readonly SocialPlatform[] = ['facebook', 'instagram']

export interface SocialFrame {
  width: number
  height: number
}

/**
 * Frames Meta renders uploaded media in. Canonical for the whole product: the
 * composer crops to these before publishing and the AI Studio converter offers
 * them as presets, so both have to read the same numbers or a converted image
 * gets re-cropped the moment it is posted.
 *
 * `facebookPost` is a photo post, not a link share — 1.91:1 is the Open Graph
 * preview ratio and does not apply to media we upload.
 */
export const SOCIAL_FRAMES = {
  square: { width: 1080, height: 1080 },
  portrait: { width: 1080, height: 1350 },
  story: { width: 1080, height: 1920 },
  facebookPost: { width: 1200, height: 1200 },
} as const satisfies Record<string, SocialFrame>

export const socialConnectionStatusSchema = z.enum(['pending', 'connected', 'disconnected'])
export type SocialConnectionStatus = z.infer<typeof socialConnectionStatusSchema>

export const createPortalLinkRequestSchema = z.object({
  platform: socialPlatformSchema,
})
export type CreatePortalLinkRequest = z.infer<typeof createPortalLinkRequestSchema>

export const createPortalLinkResponseSchema = z.object({
  url: z.string().url(),
})
export type CreatePortalLinkResponse = z.infer<typeof createPortalLinkResponseSchema>

export const socialConnectionSchema = z.object({
  platform: socialPlatformSchema,
  status: socialConnectionStatusSchema,
  username: z.string().nullable(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  channelName: z.string().nullable(),
  connectedAt: z.string().datetime().nullable(),
})
export type SocialConnection = z.infer<typeof socialConnectionSchema>

export const connectionsResponseSchema = z.object({
  connections: z.array(socialConnectionSchema),
})
export type ConnectionsResponse = z.infer<typeof connectionsResponseSchema>

export const disconnectRequestSchema = z.object({
  platform: socialPlatformSchema,
})
export type DisconnectRequest = z.infer<typeof disconnectRequestSchema>

export const crosspostPostTypeSchema = z.enum(['post', 'reel', 'story'])
export type CrosspostPostType = z.infer<typeof crosspostPostTypeSchema>

export const crosspostPostStatusSchema = z.enum([
  'draft',
  'scheduled',
  'processing',
  'posted',
  'error',
  'deleted',
  'review',
  'retrying',
])
export type CrosspostPostStatus = z.infer<typeof crosspostPostStatusSchema>

/** Facebook composer supports Page Post and Story (image) this release; Reel (video) is upcoming. */
export const facebookPostTypeSchema = z.enum(['post', 'story'])
export type FacebookPostType = z.infer<typeof facebookPostTypeSchema>

export const FACEBOOK_MAX_IMAGES = 10

export const createFacebookPostRequestSchema = z
  .object({
    type: facebookPostTypeSchema,
    text: z.string().max(5000).optional(),
    link: z.string().url().optional(),
  })
  .refine((v) => !(v.link && v.type !== 'post'), {
    message: 'A link can only be attached to a Page Post.',
    path: ['link'],
  })
export type CreateFacebookPostRequest = z.infer<typeof createFacebookPostRequestSchema>

export const crosspostPostSchema = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),
  platform: socialPlatformSchema,
  type: crosspostPostTypeSchema,
  status: crosspostPostStatusSchema,
  title: z.string(),
  text: z.string().nullable(),
  link: z.string().nullable(),
  permalink: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
  error: z.string().nullable(),
  mediaCount: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  postedAt: z.string().datetime().nullable(),
})
export type CrosspostPost = z.infer<typeof crosspostPostSchema>

export const crosspostPostResponseSchema = z.object({ post: crosspostPostSchema })
export type CrosspostPostResponse = z.infer<typeof crosspostPostResponseSchema>

/** Instagram composer supports Post (image/carousel) and Story (image) this release; Reel (video) is upcoming. */
export const instagramPostTypeSchema = z.enum(['post', 'story'])
export type InstagramPostType = z.infer<typeof instagramPostTypeSchema>

export const INSTAGRAM_MAX_IMAGES = 10

export const createInstagramPostRequestSchema = z.object({
  type: instagramPostTypeSchema,
  text: z.string().max(2200).optional(),
})
export type CreateInstagramPostRequest = z.infer<typeof createInstagramPostRequestSchema>

export const CROSSPOST_MAX_IMAGES = 10

/**
 * A single post fanned out to several platforms at once. `text` is the shared
 * caption; the per-platform blocks override it (and carry the Facebook-only
 * link). Type is unified across platforms (Reel is not offered here yet).
 */
export const createCrosspostRequestSchema = z
  .object({
    platforms: z.array(socialPlatformSchema).min(1),
    type: z.enum(['post', 'story']),
    text: z.string().max(2200).optional(),
    facebook: z
      .object({
        text: z.string().max(5000).optional(),
        link: z.string().url().optional(),
      })
      .optional(),
    instagram: z
      .object({
        text: z.string().max(2200).optional(),
      })
      .optional(),
  })
  .refine((v) => new Set(v.platforms).size === v.platforms.length, {
    message: 'Platforms must be unique.',
    path: ['platforms'],
  })
  .refine((v) => !(v.facebook?.link && v.type !== 'post'), {
    message: 'A link can only be attached to a Page Post.',
    path: ['facebook', 'link'],
  })
export type CreateCrosspostRequest = z.infer<typeof createCrosspostRequestSchema>

export const crosspostMultiResponseSchema = z.object({ posts: z.array(crosspostPostSchema) })
export type CrosspostMultiResponse = z.infer<typeof crosspostMultiResponseSchema>

export const CROSSPOST_POSTS_PAGE_SIZE = 50

export const listCrosspostPostsResponseSchema = z.object({
  posts: z.array(crosspostPostSchema),
  hasMore: z.boolean(),
})
export type ListCrosspostPostsResponse = z.infer<typeof listCrosspostPostsResponseSchema>

export const crosspostingStatusResponseSchema = z.object({
  enabled: z.boolean(),
})
export type CrosspostingStatusResponse = z.infer<typeof crosspostingStatusResponseSchema>
